import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { OfflineSyncProvider } from '@/providers/OfflineSyncProvider';
import { KeyboardShortcutsProvider } from '@/providers/KeyboardShortcutsProvider';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { PageSkeleton } from '@/components/shared/PageSkeleton';
import { LiveRegionProvider } from '@/components/shared/LiveRegion';
import { BottomNav } from '@/components/layout/BottomNav';
import { LoginPage } from '@/pages/LoginPage';
import { SignupPage } from '@/pages/SignupPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { ProductsPage } from '@/pages/ProductsPage';
import { AlertsPage } from '@/pages/AlertsPage';

// Phase 6: Pages unifiées
const UnifiedInventoryPage = lazy(() =>
  import('@/pages/UnifiedInventoryPage').then((module) => ({ default: module.UnifiedInventoryPage }))
);
const HistoryReportsPage = lazy(() =>
  import('@/pages/HistoryReportsPage').then((module) => ({ default: module.HistoryReportsPage }))
);
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import EmailConfirmPage from '@/pages/EmailConfirmPage';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { Toaster } from '@/components/ui/Toaster';
import { VoiceButton } from '@/components/voice/VoiceButton';
import { SyncStatusBar } from '@/components/shared/SyncStatusBar';
import { InstallPrompt, OfflineIndicator, UpdateBanner } from '@/components/pwa';
import { authService } from '@/services/authService';
import { initializeNotifications } from '@/services/notificationService';
import { alertService } from '@/services/alertService';
import { useAuthStore } from '@/stores/authStore';

// Lazy loaded pages (pages lourdes avec graphiques, exports, etc.)
// Note: AnalyticsPage, InsightsPage, ReportsPage, HistoryPage sont maintenant intégrés dans HistoryReportsPage
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage }))
);
const SuppliersPage = lazy(() =>
  import('@/pages/SuppliersPage').then((module) => ({ default: module.SuppliersPage }))
);
// Note: InventoryModePage et GlobalStocksPage sont maintenant intégrés dans UnifiedInventoryPage
const DebugPage = lazy(() =>
  import('@/pages/DebugPage').then((module) => ({ default: module.DebugPage }))
);
const LotsPage = lazy(() =>
  import('@/pages/LotsPage').then((module) => ({ default: module.LotsPage }))
);

// Phase 9: Recipes
const RecipesPage = lazy(() =>
  import('@/pages/RecipesPage').then((module) => ({ default: module.RecipesPage }))
);

// Phase 7: Onboarding
const OnboardingPage = lazy(() =>
  import('@/pages/OnboardingPage').then((module) => ({ default: module.OnboardingPage }))
);

// Phase 10: Pricing
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((module) => ({ default: module.PricingPage }))
);

// Legal pages
const TermsPage = lazy(() =>
  import('@/pages/TermsPage').then((module) => ({ default: module.TermsPage }))
);
const PrivacyPage = lazy(() =>
  import('@/pages/PrivacyPage').then((module) => ({ default: module.PrivacyPage }))
);

// P2 Marketing: Landing Page
const LandingPage = lazy(() =>
  import('@/pages/LandingPage').then((module) => ({ default: module.LandingPage }))
);

// Wrapper pour les pages lazy avec fallback
function LazyPage({
  children,
  message,
}: {
  children: React.ReactNode;
  message?: string;
}) {
  return (
    <Suspense fallback={<PageSkeleton message={message} />}>
      {children}
    </Suspense>
  );
}

// Phase 8: Mobile Bottom Navigation (only visible when authenticated)
function MobileBottomNav() {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Don't render while loading or if not authenticated
  if (isLoading || !isAuthenticated) {
    return null;
  }

  return <BottomNav />;
}

// P2 Marketing: Home page - Landing for unauthenticated, redirect to dashboard for authenticated
function HomePage() {
  const { isAuthenticated, isLoading } = useAuthStore();

  // Show loading while checking auth
  if (isLoading) {
    return <PageSkeleton message="Chargement..." />;
  }

  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  // Show landing page for unauthenticated users
  return (
    <LazyPage message="Chargement...">
      <LandingPage />
    </LazyPage>
  );
}

function App() {
  // Initialiser l'authentification, les notifications et les alertes au démarrage de l'app
  useEffect(() => {
    authService.initialize();
    authService.setupAuthListener();
    initializeNotifications();
    alertService.initialize();
  }, []);

  return (
    <ErrorBoundary>
      <LiveRegionProvider>
        <OfflineSyncProvider>
          <BrowserRouter>
            <KeyboardShortcutsProvider>
              {/* Skip link for keyboard navigation */}
              <a href="#main-content" className="skip-link">
                Aller au contenu principal
              </a>

              <div id="main-content" tabIndex={-1} className="outline-none">
                <Routes>
            {/* Routes publiques */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirm-email" element={<EmailConfirmPage />} />
            <Route
              path="/debug"
              element={
                <LazyPage message="Chargement du debug...">
                  <DebugPage />
                </LazyPage>
              }
            />
            {/* Phase 10: Pricing page (public) */}
            <Route
              path="/pricing"
              element={
                <LazyPage message="Chargement des tarifs...">
                  <PricingPage />
                </LazyPage>
              }
            />
            {/* Legal pages (public) */}
            <Route
              path="/terms"
              element={
                <LazyPage message="Chargement...">
                  <TermsPage />
                </LazyPage>
              }
            />
            <Route
              path="/privacy"
              element={
                <LazyPage message="Chargement...">
                  <PrivacyPage />
                </LazyPage>
              }
            />

            {/* Phase 7: Route onboarding (skip la vérification onboarding) */}
            <Route element={<ProtectedRoute skipOnboarding />}>
              <Route
                path="/onboarding"
                element={
                  <LazyPage message="Chargement...">
                    <OnboardingPage />
                  </LazyPage>
                }
              />
            </Route>

            {/* Routes protégées */}
            <Route element={<ProtectedRoute />}>
              {/* Pages chargées immédiatement (core functionality) */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/alerts" element={<AlertsPage />} />

              {/* Phase 6: Page Inventaire unifiée */}
              <Route
                path="/inventory"
                element={
                  <LazyPage message="Chargement de l'inventaire...">
                    <UnifiedInventoryPage />
                  </LazyPage>
                }
              />
              {/* Redirections des anciennes URLs vers la page unifiée */}
              <Route path="/inventory-mode" element={<Navigate to="/inventory?tab=inventory-mode" replace />} />
              <Route path="/global-stocks" element={<Navigate to="/inventory?tab=global" replace />} />

              {/* Pages chargées en lazy (moins fréquentes ou lourdes) */}
              <Route
                path="/lots"
                element={
                  <LazyPage message="Chargement des lots...">
                    <LotsPage />
                  </LazyPage>
                }
              />
              {/* Phase 9: Recipes */}
              <Route
                path="/recipes"
                element={
                  <LazyPage message="Chargement des recettes...">
                    <RecipesPage />
                  </LazyPage>
                }
              />
              <Route
                path="/suppliers"
                element={
                  <LazyPage message="Chargement des fournisseurs...">
                    <SuppliersPage />
                  </LazyPage>
                }
              />
              <Route
                path="/settings"
                element={
                  <LazyPage message="Chargement des paramètres...">
                    <SettingsPage />
                  </LazyPage>
                }
              />
              {/* Phase 6: Page Historique & Rapports unifiée */}
              <Route
                path="/history"
                element={
                  <LazyPage message="Chargement de l'historique...">
                    <HistoryReportsPage />
                  </LazyPage>
                }
              />
              {/* Redirections des anciennes URLs vers la page unifiée */}
              <Route path="/analytics" element={<Navigate to="/history?tab=analytics" replace />} />
              <Route path="/insights" element={<Navigate to="/history?tab=insights" replace />} />
              <Route path="/reports" element={<Navigate to="/history?tab=reports" replace />} />
            </Route>

            {/* P2 Marketing: Landing page for visitors, redirect for authenticated */}
            <Route path="/" element={<HomePage />} />

            {/* 404 */}
            <Route
              path="*"
              element={
                <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">404</h1>
                    <p className="text-gray-600 dark:text-gray-400">Page non trouvée</p>
                  </div>
                </div>
              }
            />
          </Routes>

          {/* Bouton de saisie vocale (visible uniquement si authentifié) */}
          <VoiceButton />

          {/* Barre de statut de synchronisation */}
          <SyncStatusBar />

          {/* Toaster pour les notifications */}
          <Toaster />

              {/* PWA Components */}
              <OfflineIndicator />
              <UpdateBanner />
              <InstallPrompt variant="banner" />

              {/* Phase 8: Mobile Bottom Navigation */}
              <MobileBottomNav />
              </div>
            </KeyboardShortcutsProvider>
          </BrowserRouter>
        </OfflineSyncProvider>
      </LiveRegionProvider>
    </ErrorBoundary>
  );
}

export default App;
